import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Copy, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LocationSelect } from "@/components/LocationSelect";

export const WineSuggestions = ({ query, onAddBottles, onTemplate, onCatalog }) => {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setItems([]);
      setCatalog([]);
      return undefined;
    }
    let alive = true;
    const t = setTimeout(() => {
      api.get("/wines", { params: { q } }).then((r) => alive && setItems(r.data.slice(0, 5))).catch(() => alive && setItems([]));
      api.get("/catalog/search", { params: { q } }).then((r) => alive && setCatalog(r.data)).catch(() => alive && setCatalog([]));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query]);

  if (!items.length && !catalog.length) return null;
  return (
    <div className="col-span-2 rounded-lg border border-[#4A3B43] bg-[#0D0A0B] p-2 -mt-2 max-h-80 overflow-y-auto" data-testid="wine-suggestions">
      {!!catalog.length && (
        <>
          <p className="px-2 pt-1 pb-2 text-[11px] uppercase tracking-widest text-[#6E665D]">Wine database · {catalog.length}{catalog.length >= 20 ? "+" : ""} match{catalog.length === 1 ? "" : "es"} — keep typing to narrow</p>
          {catalog.map((c) => (
            <button type="button" key={c.id} onClick={() => onCatalog(c)} className="w-full text-left rounded-md px-2 py-2 hover:bg-[#1E181B]" data-testid={`catalog-suggestion-${c.id}`}>
              <p className="text-sm text-[#F5F0EB] truncate">{c.display_name}</p>
              <p className="text-xs text-[#6E665D] truncate">{[c.color, c.country, c.region, c.sub_region].filter(Boolean).join(" · ")}</p>
            </button>
          ))}
        </>
      )}
      {!!items.length && <p className="px-2 pt-3 pb-2 text-[11px] uppercase tracking-widest text-[#6E665D]">Already in this cellar</p>}
      {items.map((w) => (
        <div key={w.id} className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-[#1E181B]" data-testid={`wine-suggestion-${w.id}`}>
          <span className="font-mono text-xs text-[#D4AF37] w-10 shrink-0">{w.vintage || "NV"}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#F5F0EB] truncate">{w.name}</p>
            <p className="text-xs text-[#6E665D] truncate">{w.producer}{w.box_numbers.length ? ` · box ${w.box_numbers.join(", ")}` : ""} · {w.bottle_count} btl</p>
          </div>
          <Button type="button" size="sm" onClick={() => onAddBottles(w)} className="h-8 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid={`suggestion-add-bottles-${w.id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Bottles
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => onTemplate(w)} className="h-8" data-testid={`suggestion-template-${w.id}`} title="Copy details for another vintage">
            <Copy className="w-3.5 h-3.5 mr-1" />New vintage
          </Button>
        </div>
      ))}
    </div>
  );
};

export const AddToExisting = ({ wine, onBack, onSaved, defaultLocation }) => {
  const [count, setCount] = useState(1);
  const [box, setBox] = useState("");
  const [loc, setLoc] = useState(defaultLocation || "");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/wines/${wine.id}/bottles`, { count: Number(count), box_number: box, location_id: loc || null });
      toast.success(`${count} bottle${Number(count) === 1 ? "" : "s"} added to ${wine.name}`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4 mt-2" data-testid="add-to-existing-form">
      <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm text-[#A89F91] hover:text-[#D4AF37]" data-testid="add-to-existing-back"><ArrowLeft className="w-4 h-4" />Back to new wine</button>
      <div className="rounded-lg border border-[#2E252A] bg-[#0D0A0B] p-4">
        <p className="font-mono text-sm text-[#D4AF37]">{wine.vintage || "NV"}</p>
        <p className="font-serif text-xl text-[#F5F0EB]" data-testid="add-to-existing-name">{wine.name}</p>
        <p className="text-xs text-[#A89F91]">{wine.producer} · {wine.bottle_count} bottles in cellar{wine.locations.length ? ` · ${wine.locations.join(", ")}` : ""}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input type="number" min="1" max="500" required value={count} onChange={(e) => setCount(e.target.value)} placeholder="Number of bottles" data-testid="add-to-existing-count" />
        <Input value={box} onChange={(e) => setBox(e.target.value)} placeholder="Box number (optional)" data-testid="add-to-existing-box" />
        <div className="col-span-2"><LocationSelect value={loc} onChange={setLoc} testId="add-to-existing-location" allowCreate allowPending /></div>
      </div>
      <p className="text-xs text-[#6E665D]">Each new bottle gets its own bar code. If the box already exists, the bottles take the box's location.</p>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy} className="bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="add-to-existing-save">{busy ? "Adding…" : "Add bottles"}</Button>
      </div>
    </form>
  );
};
